import { useCallback } from "react";
import { TwoColumnsLayout } from "@themes/pollinator/ui/TwoColumnsLayout";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Type } from "@themes/pollinator/ui/Type";
import { Button } from "@themes/pollinator/ui/Button";
import { ClipboardCopyButton } from "@themes/pollinator/ui/ClipboardCopyButton";
import type {
  OutreachAsset,
  OutreachLongTextItem,
  OutreachPost as OutreachPostType,
} from "./outreach.types";
import { populateOutreachVariables } from "./populateOutreachVariables";
import { OutreachItem } from "./OutreachItem";
import { OutreachLongText } from "./OutreachLongText";
import { OutreachButtonAction } from "./OutreachButtonAction";
import { OutreachPost } from "./OutreachPost";
import { OutreachPostList } from "./OutreachPostList";

export type InvitationData = {
  /** Invitation link */
  link: string;
  /** Coupon code */
  code: string;
  /** Optional name to display when multiple invitations */
  name?: string;
};

export type Props = {
  /** Organization name */
  organizationName: string;
  /** Organization type */
  organizationType: "residencies" | "schools";
  /** URL for all assets zip file */
  assetsZipUrl: string;
  /** URL for social media posts zip file */
  socialMediaPostsZipUrl: string;
  /** URL for social media stories zip file */
  socialMediaStoriesZipUrl: string;
  /** Invitation data (links and codes) */
  invitations: InvitationData[];
  /** Email outreach content */
  emailOutreachItem: OutreachLongTextItem;
  /** Newsletter blurb content */
  newsletterBlurbOutreachItem: OutreachLongTextItem;
  /** Social media content */
  socialMediaOutreachItem: OutreachLongTextItem;
  /** Social media post images */
  socialMediaPosts: OutreachPostType[];
  /** Social media story images */
  socialMediaStories: OutreachPostType[];
  /** Brand logo assets */
  logos: OutreachAsset[];
  /** Loading state */
  isLoading: boolean;
  /** Callback to create a participant invitation */
  onCreateInvitation?: () => void;
  /** Whether invitation creation is in progress */
  isCreatingInvitation?: boolean;
  /** Whether the user can create invitations */
  canCreateInvitation?: boolean;
};

/**
 * Main outreach component displaying all outreach materials for an organization.
 * Includes invitation link, coupon code, email templates, newsletter blurbs,
 * social media content, and downloadable assets.
 */
export function Outreach({
  organizationName,
  organizationType,
  assetsZipUrl,
  socialMediaPostsZipUrl,
  socialMediaStoriesZipUrl,
  invitations,
  emailOutreachItem,
  newsletterBlurbOutreachItem,
  socialMediaOutreachItem,
  socialMediaPosts,
  socialMediaStories,
  logos,
  isLoading,
  onCreateInvitation,
  isCreatingInvitation = false,
  canCreateInvitation = false,
}: Props) {
  // Use first invitation for template variable replacement
  const firstInvitation = invitations[0];
  const invitationLink = firstInvitation?.link ?? "";
  const couponCode = firstInvitation?.code ?? "";

  const replaceVariables = useCallback(
    (text: string) =>
      populateOutreachVariables(
        text,
        organizationName,
        organizationType,
        invitationLink,
        couponCode
      ),
    [organizationName, organizationType, invitationLink, couponCode]
  );

  if (isLoading) return <Loader />;

  const hasMultipleInvitations = invitations.length > 1;
  const hasInvitations = invitations.length > 0;

  const createLinkButtonLabel = isCreatingInvitation
    ? "Creating..."
    : "Create Participant Invitation";

  return (
    <>
      <TwoColumnsLayout layout="even">
        <OutreachItem
          title="Your Invite Link"
          description="Your unique invite link is already in the copy below, but you can also copy and paste it to use elsewhere. By clicking this link, your alumni and staff will be able to join Pollinator as part of your organization."
          innerClassName="flex-col items-start gap-4"
        >
          {hasInvitations ? (
            invitations.map((invitation, index) => (
              <div key={index} className="flex flex-col gap-1 w-full">
                {hasMultipleInvitations && invitation.name && (
                  <Type
                    variant="caption"
                    className="text-primary-600 font-medium"
                  >
                    {invitation.name}
                  </Type>
                )}
                <div className="flex flex-row gap-2">
                  <Type
                    as="div"
                    variant="caption"
                    className="border bg-brand-50 rounded-sm py-2 px-4 flex-grow text-primary-700 whitespace-nowrap overflow-x-auto max-w-[225px] md:max-w-[300px]"
                  >
                    {invitation.link}
                  </Type>
                  <ClipboardCopyButton
                    textToCopy={invitation.link}
                    label="Copy Link"
                  />
                </div>
              </div>
            ))
          ) : (
            <Button
              onClick={onCreateInvitation}
              disabled={isCreatingInvitation || !canCreateInvitation}
            >
              {createLinkButtonLabel}
            </Button>
          )}
        </OutreachItem>
        {hasInvitations && (
          <OutreachItem
            title="Your Code"
            description="Your code is already in the suggested copy below, but you can use it on its own in social posts, or wherever makes sense for you. Participants can enter this code on the Pollinator.coop homepage to join."
            innerClassName="flex-col items-start gap-4"
          >
            {invitations.map((invitation, index) => (
              <div key={index} className="flex flex-col gap-1 w-full">
                {hasMultipleInvitations && invitation.name && (
                  <Type
                    variant="caption"
                    className="text-primary-600 font-medium"
                  >
                    {invitation.name}
                  </Type>
                )}
                <div className="flex flex-row gap-2">
                  <Type
                    as="div"
                    variant="caption"
                    className="border bg-brand-50 rounded-sm py-2 px-4 flex-grow text-primary-700 whitespace-nowrap overflow-x-auto max-w-[225px] md:max-w-[300px]"
                  >
                    {invitation.code}
                  </Type>
                  <ClipboardCopyButton
                    textToCopy={invitation.code}
                    label="Copy Code"
                  />
                </div>
              </div>
            ))}
          </OutreachItem>
        )}
      </TwoColumnsLayout>
      <OutreachButtonAction
        href={assetsZipUrl}
        label="Download all assets"
        title="Download All Assets"
        description="The zip file below contains helpful image assets which you can use to share your Pollinator invitations on social media."
      />
      <OutreachLongText
        title="Email"
        sectionName="email"
        richText={replaceVariables(emailOutreachItem.richText)}
        plainText={replaceVariables(emailOutreachItem.plainText)}
        formattedSubject={
          emailOutreachItem.subject &&
          replaceVariables(emailOutreachItem.subject)
        }
        description={emailOutreachItem.description}
      />
      <OutreachLongText
        title="Newsletter Blurb"
        sectionName="newsletter"
        richText={replaceVariables(newsletterBlurbOutreachItem.richText)}
        plainText={replaceVariables(newsletterBlurbOutreachItem.plainText)}
        description={newsletterBlurbOutreachItem.description}
      />
      <OutreachLongText
        title="Social Media"
        sectionName="social"
        richText={replaceVariables(socialMediaOutreachItem.richText)}
        plainText={replaceVariables(socialMediaOutreachItem.plainText)}
        description={socialMediaOutreachItem.description}
      />
      <OutreachPostList
        title="Social Media Images"
        subtitle="Posts"
        label="Download Zip: All Post Images"
        href={socialMediaPostsZipUrl}
        posts={socialMediaPosts}
      />
      <OutreachPostList
        subtitle="Stories"
        label="Download Zip: All Story Images"
        href={socialMediaStoriesZipUrl}
        posts={socialMediaStories}
      />
      <OutreachItem
        title="Pollinator Brand Assets"
        description="Use these assets to create your own custom content."
        innerClassName="flex flex-col items-start gap-4"
      >
        <div className="flex flex-wrap gap-4">
          {logos.map((logo, index) => (
            <OutreachPost
              key={logo.variant}
              src={logo.url}
              label={`Logo${logo.variant ? ` (${logo.variant})` : ""} ${index + 1}`}
              background={logo.background}
            />
          ))}
        </div>
      </OutreachItem>
    </>
  );
}

